import type { SelectHTMLAttributes } from 'preact';
import { cn } from '../../lib/utils';

/**
 * Liste déroulante — **native**, habillée aux jetons shadcn.
 *
 * Écart assumé, et le plus important du lot : le `Select` de shadcn est une
 * `listbox` reconstruite en JavaScript. Ici, c'est un `<select>` du navigateur.
 *
 * Trois raisons, dans cet ordre :
 *
 * 1. Sur Android d'entrée de gamme, le sélecteur natif s'ouvre en plein écran,
 *    avec des cibles dimensionnées par le système et le défilement inertiel de
 *    la plateforme. Une `listbox` reconstruite est presque toujours moins bonne
 *    à cet endroit précis, et c'est celui où nos usagers sont.
 * 2. Elle fonctionne sans JavaScript chargé, et sans aucun octet supplémentaire.
 * 3. Le clavier, la saisie au premier caractère, la synthèse vocale et les
 *    modes de contraste forcé sont acquis, non réimplémentés — donc non
 *    régressables.
 *
 * La flèche est dessinée en fond plutôt qu'en `<svg>` frère : elle doit rester
 * dans le cadre du champ quel que soit le rendu natif du navigateur.
 */
export function Select({ className, ...reste }: SelectHTMLAttributes) {
  return (
    <select
      data-slot="select"
      className={cn(
        'flex h-10 w-full appearance-none rounded-xl border border-input bg-card py-1 pl-3 pr-9 text-base outline-none transition-colors',
        "bg-[url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='%235b6779' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m4 6 4 4 4-4'/%3E%3C/svg%3E\")] bg-[length:1rem_1rem] bg-[right_0.625rem_center] bg-no-repeat",
        'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/25',
        'aria-invalid:border-destructive aria-invalid:focus-visible:ring-destructive/25',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'md:text-sm',
        className,
      )}
      {...reste}
    />
  );
}
